'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { Clock, MapPin, Phone, UtensilsCrossed } from 'lucide-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons'
import { ISocial } from '@/lib/interfaces/interfaces'
import { barlowCondensed } from '@/lib/fonts'
import '@/sass/menu/MenuUnavailable.scss'

// Se muestra cuando el bar existe pero no tiene ninguna carta visible en el
// menú público en este momento (p.ej. todas ocultas o fuera de su horario
// programado). No es un 404: el bar existe, pero no hay carta que mostrar.
interface Props {
  barName?: string
  logoUrl?: string
  phone?: string
  address?: string
  socials?: ISocial[]
  fontFamily?: string
  fontSize?: string
}

export type MenuUnavailableProps = Props

const SOCIAL_ICONS: Record<string, any> = {
  instagram: faInstagram,
  facebook: faFacebook
}

export default function MenuUnavailable ({ barName, logoUrl, phone, address, socials, fontFamily, fontSize }: Props) {
  const [lang, setLang] = useState<'es' | 'en'>('es')

  useEffect(() => {
    setLang(navigator.language?.startsWith('es') ? 'es' : 'en')
  }, [])

  const title = lang === 'es' ? 'Carta no disponible' : 'Menu not available'
  const description = barName
    ? lang === 'es'
      ? `La carta de ${barName} no está disponible en este momento. Vuelve a intentarlo más tarde.`
      : `The menu of ${barName} is not available right now. Please try again later.`
    : lang === 'es'
      ? 'Esta carta no está disponible en este momento. Vuelve a intentarlo más tarde.'
      : 'This menu is not available right now. Please try again later.'

  return (
    <div className='menu-unavailable' style={{ fontFamily: fontFamily ?? barlowCondensed.style.fontFamily, fontSize: fontSize ?? '16px' }}>
      <div className='menu-unavailable__logo-wrap'>
        {logoUrl
          ? (
            <Image
              src={logoUrl}
              alt={barName ? `logo de ${barName}` : 'logo'}
              fill
              sizes='96px'
              priority
              style={{ objectFit: 'contain', padding: 12 }}
            />
            )
          : <UtensilsCrossed size={40} className='menu-unavailable__logo-fallback' />}
      </div>

      {barName && <h1 className='menu-unavailable__name'>{barName}</h1>}

      <div className='menu-unavailable__card'>
        <div className='menu-unavailable__card-icon'>
          <Clock size={26} />
        </div>
        <h2 className='menu-unavailable__card-title'>{title}</h2>
        <p className='menu-unavailable__card-desc'>{description}</p>
      </div>

      {(address || phone) && (
        <div className='menu-unavailable__meta'>
          {address && (
            <span>
              <MapPin size={13} />
              {address}
            </span>
          )}
          {phone && (
            <span>
              <Phone size={13} />
              {phone}
            </span>
          )}
        </div>
      )}

      {socials && socials.length > 0 && (
        <div className='menu-unavailable__socials'>
          {socials.map((social, index) => {
            const Icon = SOCIAL_ICONS[social.name]
            return Icon
              ? (
                <a key={index} href={social.url} target='_blank' rel='noreferrer'>
                  <FontAwesomeIcon icon={Icon} size='lg' />
                </a>
                )
              : null
          })}
        </div>
      )}
    </div>
  )
}